import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../Header';
import Footer from '../Footer';
import LoginModal from '../LoginModal';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, Search, Briefcase, GraduationCap, Building2, 
  MapPin, Star, MessageSquare, UserPlus, ArrowRight,
  Crown, Sparkles, Lock, Check, TrendingUp, Unlock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConnectPage = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [plans, setPlans] = useState([]);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [plansConfig, setPlansConfig] = useState(null);
  const [studentAccess, setStudentAccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/student/status`, { withCredentials: true })
        .then(r => setStudentAccess(r.data.has_access))
        .catch(() => setStudentAccess(false));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && currentUser?.id) {
      fetchUserProfile(currentUser.id);
    }
    fetchProfiles();
    fetchPlans();
    fetchPlansConfig();
    // As funções de fetch são estáveis e devem rodar na montagem e quando a autenticação mudar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isAuthenticated]);

  const fetchPlansConfig = async () => {
    try {
      const response = await axios.get(`${API}/hotmart/plans-config`);
      setPlansConfig(response.data);
    } catch (error) {
      console.error('Falha ao carregar configuração de planos:', error);
    }
  };

  const handleSubscribe = (planKey) => {
    if (!plansConfig?.plans?.[planKey]) {
      navigate('/connect/plans');
      return;
    }
    const checkoutUrl = plansConfig.plans[planKey].yearly.checkout_url;
    window.open(checkoutUrl, '_blank');
  };

  const fetchUserProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/connect/profiles/user/${userId}`);
      if (response.data.exists) {
        setUserProfile(response.data);
      }
    } catch (error) {
      console.error('Falha ao carregar perfil do usuário:', error);
    }
  };

  const fetchProfiles = async (search = '', type = '') => {
    try {
      let url = `${API}/connect/profiles?limit=12`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (type) url += `&profile_type=${type}`;
      if (userProfile?.profile_id) url += `&viewer_id=${userProfile.profile_id}`;
      
      const response = await axios.get(url);
      setProfiles(response.data);
    } catch (error) {
      console.error('Falha ao carregar perfis:', error);
      toast.error('Não foi possível carregar os perfis. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/connect/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Falha ao carregar planos:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProfiles(searchQuery, filterType);
  };

  const getPlanBadge = (plan) => {
    switch(plan) {
      case 'basic':
      case 'student':
        return <Badge className="bg-blue-600 text-white text-xs" title="Membro do plano Básico — contato liberado"><Unlock className="w-3 h-3 mr-1" />Básico</Badge>;
      case 'plus':
      case 'professional':
        return <Badge className="bg-orange-600 text-white text-xs"><Star className="w-3 h-3 mr-1" />Plus</Badge>;
      case 'premium':
      case 'company':
        return <Badge className="bg-purple-600 text-white text-xs"><Crown className="w-3 h-3 mr-1" />Premium</Badge>;
      default:
        return null;
    }
  };

  const getProfileIcon = (type) => {
    switch(type) {
      case 'student':
        return <GraduationCap className="w-4 h-4" />;
      case 'professional':
        return <Briefcase className="w-4 h-4" />;
      case 'company':
      case 'institution':
        return <Building2 className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header onLoginClick={() => setIsLoginModalOpen(true)} />
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-blue-900/30 via-zinc-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMjIiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzLTItMi00LTItNC0yLTQtMi00IDItNCAyLTIgNC0yIDQgMiAyIDQgMiA0IDIgNCAyIDQtMiA0LTIgMi00IDItNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-blue-600/20 text-blue-400 border-blue-500/30 mb-4">
              <Users className="w-4 h-4 mr-2" />
              Rede Social BIM
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              <span className="text-red-500">BIM</span> Connect
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              A maior rede de profissionais BIM da América Latina. 
              Conecte-se com estudantes, profissionais e empresas do setor.
            </p>

            {studentAccess && (
              <div className="mb-8" data-testid="connect-student-center-banner">
                <button onClick={() => navigate('/portal')}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors shadow-lg shadow-red-900/30"
                  data-testid="connect-student-center-btn">
                  <GraduationCap className="w-5 h-5" />
                  Central do Aluno — contratos, notas e certificados
                </button>
                <p className="text-gray-500 text-xs mt-2">Área exclusiva para alunos e ex-alunos AJ | BIM</p>
              </div>
            )}

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
              <div className="flex gap-2 bg-zinc-800/80 p-2 rounded-xl border border-zinc-700">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Buscar por nome, cargo, habilidade..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-transparent border-0 text-white placeholder-gray-500 focus:ring-0"
                    data-testid="connect-search-input"
                  />
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="bg-zinc-700 text-white rounded-lg px-4 py-2 border-0 focus:ring-2 focus:ring-blue-500"
                  data-testid="connect-filter-type"
                >
                  <option value="">Todos</option>
                  <option value="student">Estudantes</option>
                  <option value="professional">Profissionais</option>
                  <option value="company">Empresas</option>
                  <option value="institution">Instituições</option>
                </select>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="connect-search-btn">
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </form>

            {/* Quick Navigation */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Button
                onClick={() => navigate('/connect/feed')}
                variant="outline"
                className="border-zinc-700 bg-zinc-800/50 text-white hover:bg-zinc-700"
                data-testid="nav-feed-btn"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Feed
              </Button>
              <Button
                onClick={() => navigate('/connect/jobs')}
                variant="outline"
                className="border-green-700 bg-green-900/30 text-green-400 hover:bg-green-800/50"
                data-testid="nav-jobs-btn"
              >
                <Briefcase className="w-5 h-5 mr-2" />
                Vagas
              </Button>
              {isAuthenticated && (
                <Button
                  onClick={() => navigate('/connect/messages')}
                  variant="outline"
                  className="border-purple-700 bg-purple-900/30 text-purple-400 hover:bg-purple-800/50"
                  data-testid="nav-messages-btn"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Mensagens
                </Button>
              )}
              <Button
                onClick={() => navigate('/connect/plans')}
                variant="outline"
                className="border-orange-700 bg-orange-900/30 text-orange-400 hover:bg-orange-800/50"
                data-testid="nav-plans-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                Ver Planos
              </Button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-white">500+</div>
                <div className="text-gray-400 text-sm">Profissionais</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">50+</div>
                <div className="text-gray-400 text-sm">Empresas</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">1000+</div>
                <div className="text-gray-400 text-sm">Conexões</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Create Profile */}
      {!userProfile && (
        <section className="py-8 bg-gradient-to-r from-blue-600 to-blue-800">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <h2 className="text-2xl font-bold text-white">Faça parte da maior comunidade BIM!</h2>
                <p className="text-blue-100">Crie seu perfil gratuito e conecte-se com profissionais do setor.</p>
              </div>
              <Button 
                onClick={() => navigate('/connect/create-profile')}
                className="bg-white text-blue-600 hover:bg-blue-50 px-8"
                data-testid="create-profile-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Criar Meu Perfil
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Featured Profiles */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-white">Profissionais em Destaque</h2>
              <p className="text-gray-400">Conheça membros ativos da comunidade BIM</p>
            </div>
            <Button 
              variant="outline" 
              className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
              onClick={() => navigate('/connect/explore')}
            >
              Ver todos
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-400 mt-4">Carregando perfis...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {profiles.slice(0, 8).map((profile) => (
                <Card 
                  key={profile.id}
                  className="bg-zinc-900 border-zinc-800 hover:border-blue-500/50 transition-all cursor-pointer group"
                  onClick={() => navigate(`/connect/profile/${profile.id}`)}
                  data-testid={`profile-card-${profile.id}`}
                >
                  <CardHeader className="pb-0">
                    <div className="flex items-start justify-between">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-2xl font-bold">
                        {profile.profile_photo ? (
                          <img src={profile.profile_photo} alt={profile.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          profile.name?.charAt(0)?.toUpperCase()
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {profile.is_featured && (
                          <Badge className="bg-yellow-600/20 text-yellow-400 text-xs">
                            <Sparkles className="w-3 h-3 mr-1" />Destaque
                          </Badge>
                        )}
                        {getPlanBadge(profile.plan)}
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-4">
                    <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors line-clamp-1">
                      {profile.name}
                    </h3>
                    {profile.is_professor && (
                      <Badge className="bg-red-600/20 text-red-400 border border-red-500/30 text-xs mt-1 mb-1" data-testid="professor-badge-card">
                        <GraduationCap className="w-3 h-3 mr-1" />
                        Professor AJ | BIM
                      </Badge>
                    )}
                    <p className="text-sm text-gray-400 line-clamp-1 mb-2">{profile.headline || 'Profissional BIM'}</p>
                    
                    {profile.location && (
                      <div className="flex items-center text-xs text-gray-500 mb-3">
                        <MapPin className="w-3 h-3 mr-1" />
                        {profile.location}
                      </div>
                    )}

                    {/* Skills */}
                    {profile.skills && profile.skills.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {profile.skills.slice(0, 3).map((skill) => (
                          <Badge key={skill} variant="secondary" className="bg-zinc-800 text-gray-300 text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {profile.skills.length > 3 && (
                          <Badge variant="secondary" className="bg-zinc-800 text-gray-500 text-xs">
                            +{profile.skills.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                      <div className="flex items-center text-xs text-gray-500">
                        <Users className="w-3 h-3 mr-1" />
                        {profile.connections_count || 0} conexões
                      </div>
                      <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300 p-0 h-auto">
                        <UserPlus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {profiles.length === 0 && !loading && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Nenhum perfil encontrado</h3>
              <p className="text-gray-400">Seja o primeiro a criar um perfil e começar a conectar!</p>
              <Button 
                onClick={() => navigate('/connect/create-profile')}
                className="mt-4 bg-blue-600 hover:bg-blue-700"
              >
                Criar Meu Perfil
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-16 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-500/30 mb-4">Nossos Planos</Badge>
            <h2 className="text-3xl font-bold text-white mb-4">Desbloqueie Todo o Potencial</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Acesse recursos exclusivos para expandir sua rede e encontrar oportunidades no mercado BIM.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {/* Free Plan */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="text-center">
                  <h3 className="text-lg font-bold text-white">Gratuito</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-white">R$0</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <ul className="space-y-2">
                  {['Criar perfil completo', 'Adicionar certificações', 'Conectar com outros', 'Publicar no feed', 'Acesso básico ao BIM Map'].map((feature) => (
                    <li key={feature} className="flex items-start text-xs text-gray-300">
                      <Check className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full mt-4 border-zinc-700 text-gray-300 text-sm h-9">
                  Plano Atual
                </Button>
              </CardContent>
            </Card>

            {/* Básico Plan */}
            <Card className="bg-zinc-900 border-blue-500/50">
              <CardHeader className="pb-2">
                <div className="text-center">
                  <Badge className="bg-blue-600 text-white text-xs mb-2">
                    <GraduationCap className="w-3 h-3 mr-1" />
                    Básico
                  </Badge>
                  <div className="mt-1">
                    <span className="text-3xl font-bold text-white">R$79,90</span>
                    <span className="text-gray-400 text-sm">/ano</span>
                  </div>
                  <p className="text-xs text-gray-500">ou 12x · cobrança anual</p>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <ul className="space-y-2">
                  {['Tudo do Gratuito', 'Acesso ao BIM Map', 'Ver contatos de membros e empresas', 'Candidatar a vagas (Em breve)'].map((feature) => (
                    <li key={feature} className="flex items-start text-xs text-gray-300">
                      <Check className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-sm h-9" onClick={() => handleSubscribe('basic')}>
                  Assinar
                </Button>
              </CardContent>
            </Card>

            {/* Plus Plan (Em breve) */}
            <Card className="bg-zinc-900 border-orange-500/40 relative opacity-70">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-zinc-700 text-white text-xs">Em breve</Badge>
              </div>
              <CardHeader className="pb-2">
                <div className="text-center">
                  <Badge className="bg-orange-600 text-white text-xs mb-2">
                    <Briefcase className="w-3 h-3 mr-1" />
                    Plus
                  </Badge>
                  <div className="mt-1">
                    <span className="text-3xl font-bold text-white">R$89,90</span>
                    <span className="text-gray-400 text-sm">/ano</span>
                  </div>
                  <p className="text-xs text-gray-500">ou 12x · cobrança anual</p>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <ul className="space-y-2">
                  {['Tudo do Básico', 'Perfil em destaque', 'Badge profissional', 'Acesso às Bibliotecas BIM'].map((feature) => (
                    <li key={feature} className="flex items-start text-xs text-gray-300">
                      <Check className="w-3 h-3 text-orange-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button disabled className="w-full mt-4 bg-zinc-700 text-gray-400 cursor-not-allowed text-sm h-9">
                  Em breve
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Button 
              variant="outline" 
              className="border-orange-500 text-orange-400 hover:bg-orange-500/10"
              onClick={() => navigate('/connect/plans')}
            >
              Ver todos os detalhes dos planos
            </Button>
          </div>
        </div>
      </section>

      {/* Features Locked Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Contatos Desbloqueados</h3>
              <p className="text-gray-400 text-sm">Veja os contatos de profissionais e empresas registrados no AJ | BIM Map.</p>
              <Badge className="mt-3 bg-zinc-800 text-gray-400">
                <Lock className="w-3 h-3 mr-1" />
                Básico
              </Badge>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Destaque nos Resultados</h3>
              <p className="text-gray-400 text-sm">Apareça primeiro nas buscas e seja visto por mais pessoas.</p>
              <Badge className="mt-3 bg-zinc-800 text-gray-400">
                <Lock className="w-3 h-3 mr-1" />
                Em breve
              </Badge>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Publicar Vagas</h3>
              <p className="text-gray-400 text-sm">Publique vagas de emprego e encontre talentos BIM.</p>
              <Badge className="mt-3 bg-zinc-800 text-gray-400">
                <Lock className="w-3 h-3 mr-1" />
                Em breve
              </Badge>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ConnectPage;
